import type { PageSeo } from "@/lib/seo.functions";

type Head = {
  meta?: Array<Record<string, unknown> | undefined>;
  links?: Array<Record<string, unknown> | undefined>;
  scripts?: Array<Record<string, unknown> | undefined>;
  [k: string]: unknown;
};

const SEO_META_NAMES = new Set(["description", "robots"]);

function isSeoMeta(m: Record<string, unknown>): boolean {
  if ("title" in m) return true;
  const name = typeof m.name === "string" ? m.name : "";
  const prop = typeof m.property === "string" ? m.property : "";
  return SEO_META_NAMES.has(name) || name.startsWith("twitter:") || prop.startsWith("og:");
}

function isSeoLink(l: Record<string, unknown>): boolean {
  return l.rel === "canonical" || (l.rel === "alternate" && "hreflang" in l);
}

/** Read the backoffice SEO fetched by the root loader for the current path. */
export function pageSeoFromMatches(matches: unknown): PageSeo | null {
  const list = (matches ?? []) as Array<{ routeId?: string; loaderData?: { pageSeo?: PageSeo | null } }>;
  return list.find((m) => m.routeId === "__root__")?.loaderData?.pageSeo ?? null;
}

/** Converts backoffice SEO into TanStack head() parts. */
export function seoToHead(seo: PageSeo) {
  const meta: Array<Record<string, string>> = [{ title: seo.title ?? "" }];
  if (seo.description) meta.push({ name: "description", content: seo.description });
  if (seo.robots) meta.push({ name: "robots", content: seo.robots });
  for (const [k, v] of Object.entries(seo.og ?? {})) {
    if (!v) continue;
    meta.push({ property: k === "siteName" ? "og:site_name" : `og:${k}`, content: v });
  }
  for (const [k, v] of Object.entries(seo.twitter ?? {})) {
    if (v) meta.push({ name: `twitter:${k}`, content: v });
  }
  const links: Array<Record<string, string>> = [];
  if (seo.canonical) links.push({ rel: "canonical", href: seo.canonical });
  for (const a of seo.alternates ?? []) {
    if (a?.hreflang && a?.href) links.push({ rel: "alternate", hreflang: a.hreflang, href: a.href });
  }
  const scripts = seo.jsonLd.map((b) => ({ type: "application/ld+json", children: b }));
  return { meta, links, scripts };
}

/**
 * Apply to a leaf route head(). When the backoffice SEO is available, the
 * leaf's own SEO tags are dropped (root emits the backoffice ones); otherwise
 * the leaf's tags are kept as fallback. Hardcoded JSON-LD is always dropped —
 * structured data comes only from the backoffice.
 */
export function applySeo(matches: unknown, head: Head) {
  const h = head ?? {};
  const seo = pageSeoFromMatches(matches);
  const scripts = (h.scripts ?? []).filter((s) => s && s.type !== "application/ld+json");
  if (!seo) return { ...h, scripts } as never;
  return {
    ...h,
    meta: (h.meta ?? []).filter((m) => m && !isSeoMeta(m)),
    links: (h.links ?? []).filter((l) => l && !isSeoLink(l)),
    scripts,
  } as never;
}
